import { Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import { EMPTY_ROW, KeyphraseRow } from '../../../../../utils/keyphrases';
import { EnhancedTable, EnhancedTableHeadCell, KeyphraseTableRow, RowsPerPageOptions, RowsPerPageValues } from '../../../../Table';
import WordListDialog from '../WordListDialog';

interface KeyphraseTableProps {
	rows: KeyphraseRow[];
	updateRows: (newRows: KeyphraseRow[]) => void;
	tagOptions: string[];
	updateTagOptions: (newOptions: string[]) => void;
	errorRows: number[];
	changeInProgress?: boolean;
}

const KeyphraseTable: React.FC<KeyphraseTableProps> = ({ rows, updateRows, tagOptions, updateTagOptions, errorRows, changeInProgress }) => {
	const [page, setPage] = useState<number>(0);
	const [rowsPerPage, setRowsPerPage] = useState<RowsPerPageOptions>(RowsPerPageValues[0]);
	const [wordListDialogOpen, setWordListDialogOpen] = useState<boolean>(false);

	const headCells: EnhancedTableHeadCell[] = [
		{ id: 'rowNumber', label: '#' },
		{ id: 'pronoun', label: 'Pronoun' },
		{ id: 'max_word_allowance', label: 'Max Word Allowance' },
		{ id: 'terms', label: 'Terms' },
		{ id: 'tag', label: 'Tag' },
		{ id: 'match_whole_words', label: 'Match Whole Words', align: 'center' },
		{ id: 'delete', label: 'Delete', align: 'center' },
		{ id: 'add', label: 'Add' },
	];

	const bottomRow = {
		content: (
			<Button fullWidth onClick={() => setWordListDialogOpen(true)} data-testid="add-existing-word-list-button">
				Add an Existing Word / Phrase List
			</Button>
		),
		customSx: { padding: 0 },
	};

	const loadWords = (newRows: KeyphraseRow[]) => {
		if (rows.length === 1 && rows[0].terms === '' && rows[0].pronoun === '' && rows[0].tag === '') {
			updateRows(newRows);
		} else {
			updateRows([...rows, ...newRows]);
		}
	};

	const rowFunc = (row: KeyphraseRow, idx: number) => {
		const lastRowOnPage = idx + 1 >= (page + 1) * rowsPerPage;

		const updateRow = (row: KeyphraseRow) => {
			if (!changeInProgress) {
				const newRows = [...rows];
				newRows[idx] = row;
				updateRows(newRows);
			}
		};

		const deleteRow = (rowsToRemove?: KeyphraseRow[]) => {
			const newRows = [...rows];
			if (Object.keys(newRows).length > 1) {
				if (rowsToRemove) {
					newRows.splice(idx + 1, rowsToRemove.length);
				} else {
					newRows.splice(idx, 1);
				}
			}
			// if deleting the row would result in an empty page go back to the previous
			if (newRows.length < page * rowsPerPage + 1) {
				setPage(page - 1);
			}
			updateRows(newRows);
		};

		const addRow = (newRow?: KeyphraseRow[]) => {
			const newRows = newRow ? [...rows, ...newRow] : [...rows];
			if (!newRow) {
				newRows.splice(idx + 1, 0, EMPTY_ROW);
			}

			// if the new row would be on the next page, go to the next page
			if (lastRowOnPage) {
				setPage(page + 1);
			}
			updateRows(newRows);
		};

		return (
			<KeyphraseTableRow
				key={`keyphrase-table-row-${idx}`}
				rowIdx={idx}
				row={row}
				updateRow={updateRow}
				tagOptions={tagOptions}
				updateTagOptions={updateTagOptions}
				disableDelete={rows.length === 1}
				onDelete={deleteRow}
				onAdd={addRow}
				changeInProgress={changeInProgress}
				bottomRow={lastRowOnPage || idx + 1 === rows.length ? bottomRow : undefined}
			/>
		);
	};

	return (
		<>
			<EnhancedTable
				defaultOrderBy=""
				headers={headCells}
				rowData={rows}
				rowFunction={rowFunc}
				page={page}
				setPage={setPage}
				rowsPerPage={rowsPerPage}
				setRowsPerPage={setRowsPerPage}
				tableTitle={`Create Keyphrases`}
			/>

			{errorRows.length > 0 && (
				<Typography color="error" textAlign={'center'} data-testid="keyphrase-table-error-text">
					Errors found on row(s): <b>{errorRows.join(', ')}</b>. Please correct and re-submit
				</Typography>
			)}

			<WordListDialog open={wordListDialogOpen} setOpen={setWordListDialogOpen} updateRows={loadWords} />
		</>
	);
};

export default KeyphraseTable;
